// What the Schedule of Work needs from the job's pricing reference: the wash rate (#510), the hire
// list (#464) and the option's paint rates, all from ONE read on the version's pricing date (#530). Pure apart
// from the readers it's given.
import { optionPaintRates, type HireItemFromApi, type JobPrice, type OptionScope, type PaintProductFromApi, type PaintRate, type PricingReferenceFromApi } from '@higgins/m2q-pricing';

type Version = { fields: Record<string, unknown> } | null;
type ReadReference = (date: string) => Promise<PricingReferenceFromApi>;

/** The Schedule's option (its scope), the measure's job prices, and how to read its price-book paints on a date. */
export interface PaintsFor {
  scope: OptionScope;
  jobPrices: readonly JobPrice[];
  /** The option's price-book paints that need pricing (use-paints.ts productIdsToPrice). */
  productIds: readonly string[];
  resolve: (ids: string[], date: string) => Promise<{ products: Record<string, PaintProductFromApi | null> }>;
}

/** The version's pricing date (YYYY-MM-DD), as the Take Off prices it; `today` when it has none. */
const pricingDateOf =(version: Version, today: string): string =>
  typeof version?.fields.hig_pricingdate === 'string' ? version.fields.hig_pricingdate.slice(0, 10) : today;

export interface ScheduleReference {
  /** The branch's wash rate (the wash.rate setting), for wash hours the option doesn't rate; null when unknown. */
  washRate: number | null;
  /** The hire list, so hire lines go by their names; null when it couldn't be read. */
  hireItems: HireItemFromApi[] | null;
  /** The option's paints as pricing rates them (optionPaintRates), so litres are the priced litres; null when they couldn't be read. */
  paints: PaintRate[] | null;
}

export const NO_REFERENCE: ScheduleReference = { washRate: null, hireItems: null, paints: null };

/** Reads the reference once and takes all three from it; nothing when it can't be read (the Schedule still makes, with ticked names). */
export async function readScheduleReference(read: ReadReference, version: Version, today: string, paintsFor?: PaintsFor | null): Promise<ScheduleReference> {
  try {
    const date = pricingDateOf(version, today);
    const reference = await read(date);
    const rate = reference.settings['wash.rate'];
    const products = paintsFor?.productIds.length ? (await paintsFor.resolve([...paintsFor.productIds], date)).products : {};
    const paints = paintsFor ? optionPaintRates(paintsFor.scope, reference, products, paintsFor.jobPrices) : [];
    return { washRate: typeof rate === 'number' ? rate : null, hireItems: reference.hireItems, paints };
  } catch (err) {
    console.error('Could not load the pricing reference for the Schedule of Work', err);
    return NO_REFERENCE;
  }
}
